import { useTranslation } from '../../core/hooks/use-translation';
import type { PortalMilestone } from '../../core/models/portal-milestone';

/**
 * How far along the case is, one segment per stage, and which stage comes next.
 *
 * **Segments rather than a percentage.** Stages are not equal in length — a bank's review can
 * outlast every other stage together — so "40%" claimed a precision nobody has, and it read 100%
 * while the last stage was still under way.
 *
 * The current stage is already the page heading, so this names the one after it: the customer's
 * "and then what".
 */
export function CaseProgress({
    milestones,
    isClosed
}: {
    milestones: readonly PortalMilestone[];
    isClosed: boolean;
}) {
    const { t, language } = useTranslation();

    if (milestones.length === 0) {
        return null;
    }

    const currentIndex = milestones.findIndex((milestone) => milestone.current);
    const position =
        currentIndex >= 0 ? currentIndex + 1 : milestones.filter((milestone) => milestone.reached).length;
    const next = isClosed
        ? undefined
        : currentIndex >= 0
          ? milestones[currentIndex + 1]
          : milestones.find((milestone) => !milestone.reached);
    const label = t('portal.stage_of')
        .replace('{current}', String(position))
        .replace('{total}', String(milestones.length));

    return (
        <div>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm">
                <span className="font-semibold text-slate-700 dark:text-slate-300">{label}</span>

                {next !== undefined && (
                    <span className="min-w-0 text-slate-500 dark:text-slate-400">
                        {t('portal.next_stage')}:{' '}
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                            {language === 'kk' ? next.textKk : next.textRu}
                        </span>
                    </span>
                )}
            </div>

            <div
                role="progressbar"
                aria-label={t('portal.progress')}
                aria-valuemin={0}
                aria-valuemax={milestones.length}
                aria-valuenow={position}
                aria-valuetext={label}
                className="mt-2.5 flex gap-1"
            >
                {milestones.map((milestone) => (
                    <span
                        key={milestone.key}
                        className={`h-2 flex-1 rounded-full ${
                            milestone.current
                                ? 'bg-brand-300 dark:bg-brand-700'
                                : milestone.reached
                                  ? 'bg-brand-600 dark:bg-brand-500'
                                  : 'bg-slate-200 dark:bg-slate-800'
                        }`}
                    />
                ))}
            </div>
        </div>
    );
}
